// client/src/components/BookCardList.js
import React, { useState } from 'react';
import {
    Card, CardActionArea, CardMedia, CardContent, CardActions, Typography, IconButton,
    Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';

function BookCardList({ books, handleEditOpen, handleDelete, handleDetailOpen, deletingId, handleReturnFromList }) {

  // State für den Bestätigungsdialog
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  // *** Speichere das ganze Buch-Objekt (oder zumindest Titel + ID) ***
  const [bookToDelete, setBookToDelete] = useState(null);
  // State für Rückgabe-Ladezustand pro Buch
  const [isReturning, setIsReturning] = useState({});
  // NEU: State für Fade-Out/Entfernen
  const [removing, setRemoving] = useState({});

  // Öffnet den Dialog und speichert das Buch-Objekt
  const handleOpenConfirmDialog = (book) => { // Nimmt jetzt das ganze Buch
    setBookToDelete(book);
    setIsConfirmDialogOpen(true);
  };

  // Schließt den Dialog und setzt das Buch zurück
  const handleCloseConfirmDialog = () => {
    setBookToDelete(null);
    setIsConfirmDialogOpen(false);
  };

  // Führt die eigentliche Löschaktion aus
  const handleConfirmDelete = () => {
    if (bookToDelete?._id) { // Prüfe, ob Buch und ID vorhanden sind
      handleDelete(bookToDelete._id); // Ruft die Prop-Funktion mit der ID auf
    }
    handleCloseConfirmDialog(); // Schließt den Dialog
  };

  // Handler für Rückgabe mit Ladezustand und Fade-Out
  const handleReturn = async (loan) => {
    if (!loan?.book?._id) return;
    const bookId = loan.book._id;
    setIsReturning(prev => ({ ...prev, [bookId]: true }));
    try {
      await handleReturnFromList(loan);
      // Markiere das Buch als "removing" für Fade-Out
      setRemoving(prev => ({ ...prev, [bookId]: true }));
      // Warte auf Fade-Out (z.B. 350ms)
      await new Promise(res => setTimeout(res, 350));
    } catch (e) {
      // Fehlerbehandlung ggf. in handleReturnFromList
    } finally {
      setIsReturning(prev => ({ ...prev, [bookId]: false }));
    }
  };

  return (
    <> {/* Fragment benötigt, um Dialog außerhalb des Grids zu platzieren */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 1,
          p: 1,
        }}
      >
        {books.map((book) => {
          // isDeleting wird jetzt über die bookToDelete._id geprüft, wenn der Dialog offen ist,
          // oder über die deletingId Prop, während der API Call läuft.
          const isCurrentlyDeleting = book._id === deletingId;
          const coverUrl = book.cachedCoverUrl || book.thumbnail?.replace(/^http:\/\//, 'https://');
          const showRealCover = coverUrl && !coverUrl.endsWith('generic-book-cover.png');
          const returning = isReturning[book._id];
          const isRemoving = removing[book._id];

          return (
            <Card
              key={book._id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                opacity: isCurrentlyDeleting ? 0.5 : (isRemoving ? 0 : 1),
                transition: 'opacity 0.35s cubic-bezier(0.4,0,0.2,1)',
                overflow: 'hidden',
                minWidth: 0,
                pointerEvents: isRemoving ? 'none' : undefined,
              }}
              elevation={2}
            >
              <CardActionArea
                onClick={() => handleDetailOpen(book)}
                disabled={isCurrentlyDeleting || returning || isRemoving} // Verwende isCurrentlyDeleting und returning
                sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
              >
                {showRealCover ? (
                  <CardMedia
                    component="img"
                    sx={{ height: 160, objectFit: 'contain', pt: 1 }}
                    image={coverUrl}
                    alt={book.title}
                  />
                ) : (
                  <Box
                    sx={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.200', width: '100%' }}
                  >
                    <Typography variant="caption" color="text.secondary">Kein Cover</Typography>
                  </Box>
                )}
                <CardContent sx={{ flexGrow: 1, p: 1.5, overflow: 'hidden', width: '100%' }}>
                  <Typography
                    gutterBottom
                    variant="h6"
                    component="div"
                    sx={{
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                        mb: 0.25
                    }}
                  >
                    {book.title || 'Unbekannter Titel'}
                  </Typography>
                  <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                          fontSize: '0.75rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block'
                      }}
                  >
                    {book.authors?.join(', ') || 'Unbekannte Autoren'}
                  </Typography>
                  {/* Lagerort entfernt */}
                </CardContent>
              </CardActionArea>
              <CardActions sx={{ justifyContent: 'flex-end', pt: 0, pb: 0.5, pl: 1, pr: 1 }}>
                <IconButton size="small" aria-label="edit" onClick={() => handleEditOpen(book)} disabled={isCurrentlyDeleting || returning || isRemoving}>
                  <EditIcon fontSize="small" />
                </IconButton>
                {/* *** Übergibt das ganze book-Objekt an den Handler *** */}
                <IconButton size="small" aria-label="delete" onClick={() => handleOpenConfirmDialog(book)} disabled={isCurrentlyDeleting || returning || isRemoving}>
                  {isCurrentlyDeleting ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
                </IconButton>
                {/* Rückgabe-Button für ausgeliehene Bücher, falls handleReturnFromList übergeben */}
                {typeof book._loan !== 'undefined' && typeof handleReturnFromList === 'function' && (
                  <Button size="small" color="success" variant="outlined" onClick={() => handleReturn(book._loan)} disabled={isCurrentlyDeleting || returning || isRemoving}>
                    {returning ? <CircularProgress size={18} /> : 'Zurückgeben'}
                  </Button>
                )}
              </CardActions>
            </Card>
          );
        })}
         {books.length === 0 && (
              <Box sx={{ gridColumn: '1 / -1', p: 3, textAlign: 'center' }}>
                  <Typography>Keine Bücher gefunden.</Typography>
              </Box>
         )}
      </Box>

      {/* *** Bestätigungsdialog für Löschen (angepasst) *** */}
      <Dialog
        open={isConfirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Löschen bestätigen
        </DialogTitle>
        <DialogContent>
          {/* *** Angepasster Text mit Buchtitel *** */}
          <DialogContentText id="alert-dialog-description">
            Möchtest du das Buch "{bookToDelete?.title || 'dieses Buch'}" wirklich löschen? Diese Aktion kann aktuell nicht rückgängig gemacht werden (Soft-Delete).
          </DialogContentText>
          {/* *** Zusätzlicher Hinweistext *** */}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Gelöschte Bücher findest du im Menü. {/* Passe ggf. an, wo sie wirklich sind */}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog}>Abbrechen</Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default BookCardList;
